import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthMe, logoutFetch } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import authAPI from "../../../services/authApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { formatDateRu } from "../../../utils/appointmentLabels";
import { ageFromDob, personDisplayName } from "../../../utils/clientAppUtils";
import { membershipHeadlineChips } from "../../../utils/clientScheduleUtils";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import ClientClubMembership from "./ClientClubMembership";
import ClientClubGroups from "./ClientClubGroups";
import ClientPushSettings from "./ClientPushSettings";
import ClientSocialSettings from "./ClientSocialSettings";
import ClientChildSwitcher from "./ClientChildSwitcher";
import ClientChildPortalAccess from "./ClientChildPortalAccess";
import { useClientRefresh, dispatchClientRefresh } from "../../../hooks/useClientRefresh";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { resetClientAppChrome } from "../../../utils/clientLogout";
import s from "./clientApp.module.css";
import { Avatar, Button, Field, Input, PhoneInput, Skeleton, useToast } from "../../ui";

const ACCOUNT_BASE = "/app/profile/account";

function accountSection(pathname) {
  if (!pathname.startsWith(ACCOUNT_BASE)) return "menu";
  const rest = pathname.slice(ACCOUNT_BASE.length).replace(/^\//, "");
  return rest || "menu";
}

const MENU = [
  { id: "club", label: "Абонемент", hint: "Тариф и остаток занятий" },
  { id: "groups", label: "Группы", hint: "Группы и расписание" },
  { id: "social", label: "Оформление", hint: "Обложка, био, приватность" },
  { id: "stats", label: "Рейтинг", hint: "Место в группе по явке", link: "/app/stats" },
  { id: "data", label: "Личные данные", hint: "ФИО, телефон, email" },
  { id: "kids", label: "Дети", hint: "Семья и доступ в кабинет", parentOnly: true },
  { id: "pay", label: "Оплаты", hint: "История платежей" },
  { id: "notify", label: "Уведомления", hint: "Push и напоминания" },
  { id: "pwd", label: "Пароль", hint: "Безопасность входа" },
];

function formatRub(amount) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
  }).format(Number(amount) || 0);
}

function formatPayDate(iso) {
  if (!iso) return "";
  return formatDateRu(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ChildRow({ athlete, onOpenData, onOpenClub, portalAccess }) {
  const age = ageFromDob(athlete.date_of_birth);
  const groups = (athlete.groups || [])
    .map((g) => g.name)
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
  const name = personDisplayName(athlete.full_name) || athlete.full_name || "Спортсмен";

  return (
    <article className={s.profKidCard}>
      <div className={s.profKidCardTop}>
        <Avatar
          name={athlete.full_name || "?"}
          src={mediaUrl(athlete.photo_url)}
          size="lg"
        />
        <div className={s.profKidCardInfo}>
          <strong>{name}</strong>
          <span>
            {[
              age != null ? `${age} лет` : null,
              athlete.relation && athlete.relation !== "Ребёнок"
                ? athlete.relation
                : null,
              groups || null,
            ]
              .filter(Boolean)
              .join(" · ") || "Спортсмен клуба"}
          </span>
          {athlete.membership_label ? (
            <em className={s.profKidMembership}>{athlete.membership_label}</em>
          ) : (
            <em className={s.profKidMembershipMuted}>Без абонемента</em>
          )}
        </div>
      </div>
      <div className={s.profKidCardActions}>
        {onOpenData ? (
          <button type="button" className={s.profKidAction} onClick={onOpenData}>
            Данные
          </button>
        ) : null}
        {onOpenClub ? (
          <button type="button" className={s.profKidAction} onClick={onOpenClub}>
            Абонемент
          </button>
        ) : null}
      </div>
      {portalAccess ? (
        <div className={s.profKidPortalWrap}>{portalAccess}</div>
      ) : null}
    </article>
  );
}

export default function ClientProfileApp() {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const desktop = !isMobile;
  const { profile, tenant } = useSelector((state) => state.auth);
  const { isParentMode, athletes, athleteQueryParam, activeAthleteId, activeAthlete, reloadPortalContext, setActiveAthleteId } =
    useClientAthlete();
  const [searchParams, setSearchParams] = useSearchParams();
  const screen = accountSection(location.pathname);
  const plan = usePlanAccess();
  const [form, setForm] = useState({ full_name: "", phone: "", date_of_birth: "" });
  const [email, setEmail] = useState("");
  const [selfSnapshot, setSelfSnapshot] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [dash, setDash] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prefs, setPrefs] = useState({
    reminder_email_enabled: true,
  });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const photoInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [prof, n, deps, pay, dashboard, sched] = await Promise.all([
        clinicAPI.getMyPatientProfile(),
        clinicAPI.getNotificationPrefs(),
        clinicAPI.getDependents().catch(() => ({ dependents: [] })),
        clinicAPI.getMyPayments(athleteQueryParam).catch(() => ({ payments: [] })),
        clinicAPI.getMyClientDashboard(athleteQueryParam).catch(() => null),
        clinicAPI.getMySchedule(athleteQueryParam).catch(() => ({ sessions: [] })),
      ]);
      const p = prof.patient;
      const nextForm = {
        full_name: p?.full_name || "",
        phone: p?.phone || "",
        date_of_birth: p?.date_of_birth
          ? String(p.date_of_birth).slice(0, 10)
          : "",
      };
      const nextPhoto = p?.photo_url || null;
      const nextEmail = p?.email || "";
      setSelfSnapshot({
        form: nextForm,
        photoUrl: nextPhoto,
        email: nextEmail,
      });
      setForm(nextForm);
      setEmail(nextEmail);
      setPhotoUrl(nextPhoto);
      setDependents(deps.dependents || []);
      setPayments(pay.payments || []);
      setDash(dashboard);
      setSchedule(sched.sessions || []);
      setPrefs({
        reminder_email_enabled: n.reminder_email_enabled !== false,
      });
    } finally {
      setLoading(false);
    }
  }, [athleteQueryParam]);

  useEffect(() => {
    load();
  }, [load]);

  useClientRefresh(load);

  useEffect(() => {
    if (screen !== "data") return;
    const viewing =
      isParentMode && activeAthlete && !activeAthlete.is_self;
    if (viewing) {
      setForm({
        full_name: activeAthlete.full_name || "",
        phone: activeAthlete.phone || "",
        date_of_birth: activeAthlete.date_of_birth
          ? String(activeAthlete.date_of_birth).slice(0, 10)
          : "",
      });
      setPhotoUrl(activeAthlete.photo_url || null);
      setEmail(activeAthlete.portal_login_email || "");
      return;
    }
    if (selfSnapshot) {
      setForm(selfSnapshot.form);
      setPhotoUrl(selfSnapshot.photoUrl);
      setEmail(selfSnapshot.email);
    }
  }, [screen, activeAthleteId, activeAthlete, isParentMode, selfSnapshot]);

  useEffect(() => {
    const pay = searchParams.get("payment");
    if (!pay) return;
    if (pay === "success") {
      toast.success("Оплата прошла успешно");
      load();
      dispatchClientRefresh();
    } else if (pay === "fail") {
      toast.error("Оплата не завершена");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, load, toast]);

  useEffect(() => {
    if (screen === "menu") return;
    if (screen === "social" && !plan.memberSocial) {
      navigate(ACCOUNT_BASE, { replace: true });
      return;
    }
    if (!MENU.some((m) => m.id === screen && !m.link)) {
      navigate(ACCOUNT_BASE, { replace: true });
    }
  }, [screen, navigate, plan.memberSocial]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const forChild =
        isParentMode && activeAthlete && !activeAthlete.is_self
          ? { athlete_id: activeAthlete.id }
          : {};
      const r = await clinicAPI.uploadMyPatientPhoto(file, forChild);
      const url = r.photo_url || r.patient?.photo_url || null;
      const stamped = url
        ? `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`
        : null;
      setPhotoUrl(stamped);
      if (!forChild.athlete_id) {
        setSelfSnapshot((prev) =>
          prev ? { ...prev, photoUrl: stamped } : prev,
        );
        dispatch(fetchAuthMe());
      }
      await reloadPortalContext();
      toast.success("Фото обновлено");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const viewing =
        isParentMode && activeAthlete && !activeAthlete.is_self;
      if (viewing && screen === "data") {
        await clinicAPI.updateFamilyAthleteProfile(activeAthlete.id, {
          full_name: form.full_name,
          phone: form.phone,
          date_of_birth: form.date_of_birth || null,
        });
        await reloadPortalContext();
        toast.success("Данные ребёнка сохранены");
      } else {
        const profileForm =
          screen === "data" ? form : selfSnapshot?.form || form;
        await clinicAPI.updateMyPatientProfile({
          full_name: profileForm.full_name,
          phone: profileForm.phone,
          date_of_birth: profileForm.date_of_birth || null,
          reminder_email_enabled: prefs.reminder_email_enabled,
        });
        await clinicAPI.updateNotificationPrefs(prefs);
        if (screen === "data") {
          setSelfSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  form: { ...form },
                }
              : prev,
          );
        }
        toast.success("Сохранено");
        dispatch(fetchAuthMe());
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwd.next.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      toast.success("Пароль изменён");
      setPwd({ current: "", next: "" });
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const logout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    resetClientAppChrome();
    dispatch(logoutFetch());
    navigate("/login", { replace: true });
  };

  const mergedFamilyChildren = useMemo(() => {
    const depById = new Map(
      dependents
        .filter((d) => d.athlete_id || d.id)
        .map((d) => [String(d.athlete_id || d.id), d]),
    );
    const fromAthletes = isParentMode ? athletes.filter((a) => !a.is_self) : [];
    if (fromAthletes.length) {
      return fromAthletes.map((a) => ({
        ...depById.get(String(a.id)),
        ...a,
      }));
    }
    return dependents.filter((d) => d.athlete_id);
  }, [athletes, dependents, isParentMode]);

  const updateChildPortal = useCallback((athleteId, patch) => {
    setDependents((prev) =>
      prev.map((d) =>
        String(d.athlete_id || d.id) === String(athleteId) ? { ...d, ...patch } : d,
      ),
    );
    reloadPortalContext();
  }, [reloadPortalContext]);

  if (loading) {
    return <Skeleton height={300} style={{ borderRadius: 16 }} />;
  }

  const membership = dash?.membership;
  const headChips = membershipHeadlineChips(membership, dash?.stats);
  const displayEmail =
    email && !email.includes("@patient.internal") ? email : null;
  const activeMenu = MENU.find((m) => m.id === screen);

  const linkedAthleteIds = new Set(athletes.map((a) => String(a.id)));
  const extraDependents = dependents.filter(
    (d) => !d.athlete_id || !linkedAthleteIds.has(String(d.athlete_id)),
  );
  const showKidsMenu =
    isParentMode || extraDependents.length > 0;
  const visibleMenu = MENU.filter(
    (item) =>
      (!item.parentOnly || showKidsMenu) &&
      (item.id !== "social" || plan.memberSocial),
  );

  const viewingChild =
    isParentMode && activeAthlete && !activeAthlete.is_self;
  const showSectionSwitcher =
    isParentMode &&
    athletes.length > 1 &&
    (screen === "club" ||
      screen === "groups" ||
      screen === "pay" ||
      screen === "data");

  const renderPanel = () => {
    if (screen === "club") {
      return (
        <>
          {showSectionSwitcher ? (
            <ClientChildSwitcher variant="inline" label="Чей абонемент" />
          ) : null}
          <ClientClubMembership
            dash={dash}
            patientId={activeAthleteId || profile?.id}
            onChanged={() => {
              clinicAPI
                .getMyClientDashboard(athleteQueryParam)
                .then((d) => setDash(d))
                .catch(() => {});
            }}
          />
        </>
      );
    }
    if (screen === "groups") {
      return (
        <>
          {showSectionSwitcher ? (
            <ClientChildSwitcher variant="inline" label="Чьи группы" />
          ) : null}
          <ClientClubGroups dash={dash} schedule={schedule} />
        </>
      );
    }
    if (screen === "social") {
      return <ClientSocialSettings />;
    }
    if (screen === "data") {
      return (
        <>
          {showSectionSwitcher ? (
            <ClientChildSwitcher variant="inline" label="Чьи данные" />
          ) : null}
          <button
            type="button"
            className={s.profDataPhotoBtn}
            onClick={() => photoInputRef.current?.click()}
            disabled={photoUploading}
          >
            <Avatar
              name={form.full_name || "?"}
              src={mediaUrl(photoUrl)}
              size="lg"
            />
            <span className={s.profDataPhotoHint}>
              {photoUploading ? "Загрузка…" : "Нажмите, чтобы сменить фото"}
            </span>
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onPhoto}
          />
          <Field label="ФИО">
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
          </Field>
          <Field label="Дата рождения">
            <Input
              type="date"
              value={form.date_of_birth || ""}
              onChange={(e) =>
                setForm({ ...form, date_of_birth: e.target.value })
              }
            />
          </Field>
          <Field label="Телефон">
            <PhoneInput
              value={form.phone || ""}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </Field>
          {!viewingChild && displayEmail ? (
            <Field label="Email">
              <Input value={displayEmail} disabled />
            </Field>
          ) : null}
          {viewingChild && email ? (
            <Field
              label="Email для входа"
              hint="Меняется в разделе «Дети»"
            >
              <Input value={email} disabled />
            </Field>
          ) : null}
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? "…" : "Сохранить"}
          </Button>
        </>
      );
    }
    if (screen === "kids") {
      return (
        <>
          <p className={s.profKidsLead}>
            Профили детей, привязанные к вашему аккаунту. Данные, абонемент и
            отдельный вход в кабинет — здесь. Добавить ребёнка может только
            администратор клуба.
          </p>

          {mergedFamilyChildren.length === 0 && extraDependents.length === 0 ? (
            <div className={s.profKidsEmpty}>
              <p>Пока нет привязанных детей</p>
              <small>
                Обратитесь к администратору клуба, чтобы связать профили детей с
                вашим аккаунтом.
              </small>
            </div>
          ) : null}

          <div className={s.profKidsGrid}>
            {mergedFamilyChildren.map((athlete) => (
              <ChildRow
                key={athlete.id}
                athlete={athlete}
                onOpenData={() => {
                  setActiveAthleteId(athlete.id);
                  navigate(`${ACCOUNT_BASE}/data`);
                }}
                onOpenClub={() => {
                  setActiveAthleteId(athlete.id);
                  navigate(`${ACCOUNT_BASE}/club`);
                }}
                portalAccess={
                  <ClientChildPortalAccess
                    athlete={athlete}
                    onUpdated={(patch) => updateChildPortal(athlete.id, patch)}
                  />
                }
              />
            ))}

            {extraDependents.map((d) => (
              <ChildRow
                key={d.id}
                athlete={{
                  full_name: d.full_name,
                  date_of_birth: d.date_of_birth,
                  relation: d.relation,
                  groups: [],
                  membership_label: null,
                }}
              />
            ))}
          </div>
        </>
      );
    }
    if (screen === "pay") {
      return (
        <>
          {showSectionSwitcher ? (
            <ClientChildSwitcher variant="inline" label="Чьи оплаты" />
          ) : null}
          {payments.length === 0 ? (
            <div className={s.profPayEmpty}>
              <span className={s.profPayEmptyIcon} aria-hidden>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </span>
              <p>Оплат пока нет</p>
              <small>Здесь появятся платежи, которые отметил тренер или администратор</small>
            </div>
          ) : (
            <ul className={s.profPayList}>
              {payments.map((p) => (
                <li key={p.id} className={s.profPayCard}>
                  <div className={s.profPayCardTop}>
                    <div className={s.profPayCardMain}>
                      <strong>{p.description || "Оплата"}</strong>
                      <span className={s.profPayDate}>{formatPayDate(p.date)}</span>
                    </div>
                    <span className={s.profPayAmount}>{formatRub(p.amount)}</span>
                  </div>
                  <div className={s.profPayCardMeta}>
                    {p.method_label ? (
                      <span className={s.profPayBadge}>{p.method_label}</span>
                    ) : null}
                    {p.status === "paid" ? (
                      <span className={`${s.profPayBadge} ${s.profPayBadgeOk}`}>
                        Оплачено
                      </span>
                    ) : null}
                  </div>
                  {p.notes ? <p className={s.profPayNote}>{p.notes}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      );
    }
    if (screen === "notify") {
      return (
        <>
          <label className={s.profCheck}>
            <input
              type="checkbox"
              checked={prefs.reminder_email_enabled}
              onChange={(e) =>
                setPrefs({ ...prefs, reminder_email_enabled: e.target.checked })
              }
            />
            Email о расписании за день до занятия
          </label>
          <p className={s.profPayNote}>
            Push о тренировках — включите уведомления в приложении ниже (нужно
            добавить клуб на главный экран). Остальные события (чат, опросы) —
            через push или email в профиле.
          </p>
          <ClientPushSettings />
          <Button type="button" size="sm" onClick={save} disabled={saving}>
            {saving ? "…" : "Сохранить"}
          </Button>
        </>
      );
    }
    if (screen === "pwd") {
      return (
        <>
          <Field label="Текущий пароль">
            <Input
              type="password"
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            />
          </Field>
          <Field label="Новый пароль">
            <Input
              type="password"
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            />
          </Field>
          <Button type="button" variant="secondary" onClick={changePassword}>
            Сменить пароль
          </Button>
        </>
      );
    }
    return null;
  };

  if (screen !== "menu") {
    return (
      <div className={s.profPage}>
        <header className={s.profSubHead}>
          <button
            type="button"
            className={s.profBackBtn}
            onClick={() => navigate(ACCOUNT_BASE)}
          >
            ← {desktop ? "Настройки" : "Назад"}
          </button>
          <h1>{activeMenu?.label}</h1>
          {activeMenu?.hint ? <p>{activeMenu.hint}</p> : null}
        </header>
        <div
          className={
            screen === "social" ? s.profSocialWrap : s.profSubBody
          }
        >
          {renderPanel()}
        </div>
      </div>
    );
  }

  return (
    <div className={s.profPage}>
      {isMobile ? (
        <header className={s.profSubHead}>
          <button
            type="button"
            className={s.profBackBtn}
            onClick={() => navigate("/app/profile")}
          >
            ← Профиль
          </button>
          <h1>Настройки</h1>
          <p>Абонемент, группы и аккаунт</p>
        </header>
      ) : null}

      <div className={s.profAccountUser}>
        <button
          type="button"
          className={s.profAccountUserMain}
          onClick={() => navigate(`${ACCOUNT_BASE}/data`)}
        >
          <div className={s.profAccountUserAvatar}>
            <Avatar
              name={profile?.full_name || form.full_name || "?"}
              src={mediaUrl(photoUrl || profile?.photo_url)}
              size="md"
            />
          </div>
          <div className={s.profAccountUserText}>
            <strong>{form.full_name || profile?.full_name}</strong>
            <span>{tenant?.name || "Клуб"}</span>
            <span className={s.profAccountUserEdit}>Личные данные и фото</span>
          </div>
          <span className={s.profAccountUserChevron} aria-hidden>
            ›
          </span>
        </button>
        {headChips.length > 0 ? (
          <div className={s.profAccountUserChips}>
            {headChips.map((chip) => (
              <span
                key={chip.key}
                className={chip.accent ? `${s.profChip} ${s.profChipAccent}` : s.profChip}
              >
                {chip.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <nav className={s.profNavList} aria-label="Настройки профиля">
        {visibleMenu.map((item) =>
          item.link ? (
            <Link key={item.id} to={item.link} className={s.profNavRow}>
              <span className={s.profNavText}>
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
              <span className={s.profNavChevron} aria-hidden>
                ›
              </span>
            </Link>
          ) : (
            <button
              key={item.id}
              type="button"
              className={s.profNavRow}
              onClick={() => navigate(`${ACCOUNT_BASE}/${item.id}`)}
            >
              <span className={s.profNavText}>
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
              <span className={s.profNavChevron} aria-hidden>
                ›
              </span>
            </button>
          ),
        )}
      </nav>

      {isMobile ? (
        <button
          type="button"
          className={s.profLogout}
          onClick={logout}
          disabled={loggingOut}
        >
          {loggingOut ? "Выход…" : "Выйти из аккаунта"}
        </button>
      ) : null}
    </div>
  );
}
